import { safeSessionRead, safeSessionRemove } from '@fpo3/ts-commons-core/safe-session-storage';

/**
 * sessionStorage keys used to coordinate state across the Google OAuth
 * reauth round-trip between `GoogleReauthBanner` and
 * `initiateGoogleAuthRedirect`.
 *
 * Prefixed with `famfam:` per `.claude/rules/typescript.md` → "Browser Storage
 * Key Naming" to prevent collisions when multiple services share the same
 * origin through dev-proxy.
 */
export const REAUTH_STORAGE_KEYS = {
  /** Marks that a Google OAuth reconnection is in flight. */
  reconnect: 'famfam:google-reconnect',
  /** Optional pathname to redirect back to after a cross-page reconnect. */
  reconnectReturn: 'famfam:google-reconnect-return',
  /** Internal Account row id of the specific account being reconnected (used to scope the post-reconnect DELETE). */
  reconnectAccountId: 'famfam:google-reconnect-account-id',
} as const;

/**
 * Legacy, unprefixed keys used before the `famfam:` migration. Retained for
 * one release so in-flight OAuth round-trips started before the deploy still
 * complete correctly. On any read, values under these legacy keys are
 * returned and the legacy keys are immediately removed so a subsequent read
 * won't re-pick-up the same value and new writes only use the prefixed key.
 */
const LEGACY_REAUTH_STORAGE_KEYS = {
  reconnect: 'google-reconnect',
  reconnectReturn: 'google-reconnect-return',
  reconnectAccountId: 'google-reconnect-account-id',
} as const;

/**
 * Reads a reauth storage key, falling back to the pre-migration key for
 * in-flight OAuth round-trips. Migrates by deleting the legacy value on
 * read so writes using only the new key are immediately authoritative.
 */
export function readReauthStorageKey(key: keyof typeof REAUTH_STORAGE_KEYS): string | null {
  const prefixed = safeSessionRead(REAUTH_STORAGE_KEYS[key]);
  if (prefixed != null) return prefixed;
  const legacy = safeSessionRead(LEGACY_REAUTH_STORAGE_KEYS[key]);
  if (legacy != null) {
    safeSessionRemove(LEGACY_REAUTH_STORAGE_KEYS[key]);
    return legacy;
  }
  return null;
}

/**
 * Removes a reauth storage key and its legacy counterpart in one call.
 */
export function removeReauthStorageKey(key: keyof typeof REAUTH_STORAGE_KEYS): void {
  safeSessionRemove(REAUTH_STORAGE_KEYS[key], LEGACY_REAUTH_STORAGE_KEYS[key]);
}
